import React, { useEffect, useState } from 'react';

import useQueryPropertiesByOrgIdHook from '@fd/customHooks/useQueryPropertiesByOrgIdHook';
import { GetBrandsByOrgId200Response } from '@flipdish/orgmanagement';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { getBrandsForOrg } from 'components/RMS/organisation.services';
import { getBrandsForOrgKey } from 'components/Settings/Brands/brands.services';
import { Translate } from 'react-localize-redux';
import { connect } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { compose } from 'recompose';

import PageLayout from '../../../../ui/Layout';
import PaperContainer from '../../../../ui/Layout/PaperContainer';
import {
  createBankAccountAndConnectedAccount,
  getBankAccountDetails,
  getCountriesWithFieldDefinitions,
} from '../banking.actions';
import { getBankAccountsForBrandQuery, getVatNumberForOrgQuery } from '../banking.queries';
import { getBankAccountListUrl, getLatestBankAccount } from '../banking.selectors';
import AddAccountForm from '../components/AddAccountForm';
import VerifyYourDetailsModal from '../components/VerifyYourDetailsModal';
import { PortalFeatureFlags } from '../../../../services/featureFlags/portalFeatureFlags';
import { useFeatureFlagValue } from '../../../../services/featureFlags/useFeatureFlagValue';

type OuterProps = {
  hideBackButton?: boolean;
  goToNextStep?: string;
};

type BrandsForOrgResponse = { data: GetBrandsByOrgId200Response };

export type Props = MappedState & MappedDispatch & OuterProps;
const BankingCreate = (props: Props) => {
  const { bankingRoute, hideBackButton = false, goToNextStep, orgId, currentBrandId } = props;
  const [isSubmitSuccess, setSubmitSuccess] = useState(false);
  const [isDialogOpen, setDialogOpen] = useState(false);
  const [shouldNavigateAway, setShouldNavigateAway] = useState(false);
  const [prePopulateVatNumber, setPrePopulateVatNumber] = useState('');
  const { createConnect, newBankAccount, getCountriesFieldDefinitions } = props;
  const history = useHistory();
  const queryClient = useQueryClient();
  const hasRunRef = React.useRef<string | null>(null);
  const gbIbanAutofillEnabled = useFeatureFlagValue(
    PortalFeatureFlags.bankAccount_gbIbanAutofill,
    false
  );
  const submitForm = (stripeSupportedCountry: boolean) => {
    setSubmitSuccess(true);
    if (stripeSupportedCountry) {
      setDialogOpen(true);
    } else {
      setShouldNavigateAway(true);
    }
  };

  const cachedBrandsForOrg = queryClient.getQueryData<BrandsForOrgResponse>([
    getBrandsForOrgKey,
    orgId,
  ]);
  const { properties } = useQueryPropertiesByOrgIdHook(orgId);

  const { data: brandData } = useQuery<BrandsForOrgResponse>({
    queryKey: [getBrandsForOrgKey, orgId],
    queryFn: () => getBrandsForOrg(orgId),
    enabled: !!orgId,
    initialData: cachedBrandsForOrg,
  });

  useEffect(() => {
    getCountriesFieldDefinitions();
  }, []);

  useEffect(() => {
    if (newBankAccount) return;
    if (!brandData?.data?.data?.length) return;

    if (properties.length > 1) {
      return;
    }

    const brands = brandData?.data?.data ?? [];

    const runKey = `${orgId}-${currentBrandId}`;
    if (hasRunRef.current === runKey) return;
    hasRunRef.current = runKey;

    const brandIds =
      brands.length > 1
        ? brands.filter((brand) => brand.brandId !== currentBrandId).map((brand) => brand.brandId)
        : [];

    const run = async () => {
      try {
        if (brands.length > 1) {
          for (const brandId of brandIds) {
            const accounts = await getBankAccountsForBrandQuery(queryClient, brandId as string);

            if (accounts?.length) {
              return;
            }
          }
        }
        const vatNumberResponse = await getVatNumberForOrgQuery(queryClient, orgId);

        const vatNumber = vatNumberResponse?.data?.vatNumber;
        const vatRegistered = vatNumberResponse?.data?.vatRegistered;

        if (vatNumberResponse?.status === 200 && vatRegistered === 'yes' && vatNumber) {
          setPrePopulateVatNumber(vatNumber as string);
        }
      } catch (error) {
        console.error('Error getting bank accounts or vat number', error);
      }
    };

    void run();
  }, [brandData, newBankAccount, queryClient, orgId, currentBrandId, properties]);

  // This is the only way this works, otherwise you'd see a blank screen after confirming the popup
  // The error would be: A history supports only one prompt at a time Error Component Stack
  // at PreventNavigation (PreventNavigation.tsx:10:30) at AddAccountForm (AddAccountForm.tsx:60:11)
  useEffect(() => {
    if (shouldNavigateAway && bankingRoute) {
      history.push(bankingRoute);
    }
  }, [shouldNavigateAway, bankingRoute, history]);

  return (
    <PageLayout
      strictToParent
      toParent={hideBackButton ? '' : './'}
      title={<Translate id="Add_new_bank_account" />}
      documentTitle="Banking"
    >
      <PaperContainer>
        <AddAccountForm
          submit={createConnect}
          onSubmitSuccess={submitForm}
          isSubmitSuccess={isSubmitSuccess}
          prePopulateVatNumber={prePopulateVatNumber}
          gbIbanAutofillEnabled={gbIbanAutofillEnabled}
        />
        {newBankAccount && (
          <VerifyYourDetailsModal
            account={newBankAccount}
            isDialogOpen={isDialogOpen}
            setDialogOpen={setDialogOpen}
            onClose={() => {
              setDialogOpen(false);
              setShouldNavigateAway(true);
            }}
          />
        )}
      </PaperContainer>
    </PageLayout>
  );
};

type MappedState = ReturnType<typeof mapStateToProps>;
const mapStateToProps = (state: AppState) => {
  const bankingRoute = getBankAccountListUrl(state);
  const newBankAccount = getLatestBankAccount(state);

  return {
    bankingRoute,
    newBankAccount,
    orgId: state.rms.currentOrg?.orgId ?? '',
    currentBrandId: state.currentApp.AppId ?? '',
  };
};

type MappedDispatch = ReturnType<typeof mapDispatchToProps>;
const mapDispatchToProps = (dispatch: ThunkDispatch) => {
  return {
    createConnect: (bankAccount) => {
      return dispatch(createBankAccountAndConnectedAccount(bankAccount, true));
    },
    getBankAccountDetails: (accountId) => {
      return dispatch(getBankAccountDetails(accountId));
    },
    getCountriesFieldDefinitions: () => {
      dispatch(getCountriesWithFieldDefinitions());
    },
  };
};

export default compose<Props, OuterProps>(connect(mapStateToProps, mapDispatchToProps))(
  BankingCreate
);
